import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

/**
 * Per-event cart stored in localStorage. Scope is intentionally single-event
 * (adding a tier from a different event clears the cart) — cross-event carts
 * complicate Razorpay (one receipt), discounts (per-event), and T&C UX.
 *
 * Shape:
 *   {
 *     event_id: "<uuid>",
 *     items: [{ tier_id, tier_name, quantity, unit_price, date_id?, date_label? }]
 *   }
 */
const KEY = 'unight_cart_v1';
const CartContext = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { event_id: null, items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { event_id: null, items: [] };
    return parsed;
  } catch {
    return { event_id: null, items: [] };
  }
}

function writeStorage(cart) {
  try { localStorage.setItem(KEY, JSON.stringify(cart)); } catch { /* quota, ignore */ }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => readStorage());

  useEffect(() => { writeStorage(cart); }, [cart]);

  /** Add N tickets of a tier. If the cart was for a different event, clears
   * it first and warns the caller via the return value. */
  const addItem = useCallback((eventId, item, { onEventSwitch } = {}) => {
    setCart((prev) => {
      const switching = prev.event_id && prev.event_id !== eventId && prev.items.length > 0;
      if (switching && typeof onEventSwitch === 'function') onEventSwitch();
      const base = switching || !prev.event_id ? { event_id: eventId, items: [] } : prev;
      // Merge with an existing item of same tier + date_id (same cart line)
      const key = (it) => `${it.tier_id}::${it.date_id || ''}`;
      const existingIdx = base.items.findIndex((it) => key(it) === key(item));
      let items;
      if (existingIdx >= 0) {
        items = base.items.map((it, i) => (i === existingIdx
          ? { ...it, quantity: Math.max(1, Math.min(20, (it.quantity || 0) + item.quantity)) }
          : it));
      } else {
        items = [...base.items, { ...item, quantity: Math.max(1, Math.min(20, item.quantity || 1)) }];
      }
      return { event_id: eventId, items };
    });
  }, []);

  const updateQuantity = useCallback((lineKey, quantity) => {
    setCart((prev) => {
      const q = Math.max(0, Math.min(20, Number(quantity) || 0));
      if (q === 0) {
        const items = prev.items.filter((it) => `${it.tier_id}::${it.date_id || ''}` !== lineKey);
        return { ...prev, items, event_id: items.length ? prev.event_id : null };
      }
      const items = prev.items.map((it) => (
        `${it.tier_id}::${it.date_id || ''}` === lineKey ? { ...it, quantity: q } : it
      ));
      return { ...prev, items };
    });
  }, []);

  const removeItem = useCallback((lineKey) => updateQuantity(lineKey, 0), [updateQuantity]);

  const clear = useCallback(() => setCart({ event_id: null, items: [] }), []);

  const totals = useMemo(() => {
    const subtotal = cart.items.reduce(
      (s, it) => s + Number(it.unit_price || 0) * Number(it.quantity || 0), 0,
    );
    const count = cart.items.reduce((s, it) => s + Number(it.quantity || 0), 0);
    const allFree = cart.items.length > 0 && cart.items.every((it) => Number(it.unit_price || 0) <= 0);
    const hasPaid = cart.items.some((it) => Number(it.unit_price || 0) > 0);
    return { subtotal, count, allFree, hasPaid };
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    items: cart.items,
    eventId: cart.event_id,
    totals,
    addItem,
    updateQuantity,
    removeItem,
    clear,
  }), [cart, totals, addItem, updateQuantity, removeItem, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
